import { useMemo } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useAgency } from '@/hooks/useAgency'
import { PlanType } from '@/types'
import { TierFeatures, TIER_FEATURES, getPlanFeatures, getPlanDisplayName, getNextTier } from '@/constants/pricingTiers'

export interface UseTierFeaturesReturn {
  // Current tier
  tier: PlanType
  tierDisplayName: string

  // Feature flags - all boolean
  seoManual: boolean
  seoScheduled: boolean
  performanceManual: boolean
  performanceScheduled: boolean
  uptimeMonitoring: boolean
  uptimeInstantNotifications: boolean
  domainMonitoring: boolean
  imageHosting: boolean
  tagManager: boolean
  bulkExport: boolean
  bulkImport: boolean
  brandedReports: boolean
  hourPackages: boolean
  quotes: boolean
  whiteLabel: boolean
  changelog: boolean
  secureCredentials: boolean

  // Helpers
  canUse: (feature: keyof TierFeatures) => boolean
  nextTier: PlanType | null
  isLoading: boolean
}

/**
 * Hook to check what features are available for the current user's tier.
 * Returns feature flags and helper functions based on agency plan type.
 */
export function useTierFeatures(): UseTierFeaturesReturn {
  const { user } = useAuth()
  const { agency, loading } = useAgency(user?.agency_id)

  const tier = useMemo<PlanType>(() => {
    // Super admins get agency-level features
    if (user?.role === 'super_admin') {
      return 'agency'
    }

    return agency?.plan_type ?? 'free'
  }, [user?.role, agency?.plan_type])

  const features = useMemo<TierFeatures>(() => {
    return getPlanFeatures(tier)
  }, [tier])

  const canUse = useMemo(() => {
    return (feature: keyof TierFeatures): boolean => {
      return features[feature] ?? false
    }
  }, [features])

  return {
    // Current tier info
    tier,
    tierDisplayName: getPlanDisplayName(tier),

    // All feature flags spread out for easy destructuring
    seoManual: features.seoManual,
    seoScheduled: features.seoScheduled,
    performanceManual: features.performanceManual,
    performanceScheduled: features.performanceScheduled,
    uptimeMonitoring: features.uptimeMonitoring,
    uptimeInstantNotifications: features.uptimeInstantNotifications,
    domainMonitoring: features.domainMonitoring,
    imageHosting: features.imageHosting,
    tagManager: features.tagManager,
    bulkExport: features.bulkExport,
    bulkImport: features.bulkImport,
    brandedReports: features.brandedReports,
    hourPackages: features.hourPackages,
    quotes: features.quotes,
    whiteLabel: features.whiteLabel,
    changelog: features.changelog,
    secureCredentials: features.secureCredentials,

    // Helpers
    canUse,
    nextTier: getNextTier(tier),
    isLoading: loading,
  }
}
